import axios from 'axios'
import moment from 'moment'
import config from '@src/config'
import getApiURL from '@utils/getApiURL'
import { PAUSED_GAS_PROVIDERS } from '@utils/isEversourceGasLead'

const urlParams = new URLSearchParams(document.location.search)

/**
 * Normalizes phone number by removing +1 prefix and all non-digit characters
 * @param {string} phone - Phone number to normalize
 * @returns {string} Normalized phone number (digits only)
 */
const normalizePhoneNumber = (phone) => {
  return phone ? phone.replace('+1', '').replace(/[^\d]/g, '') : ''
}

/**
 * Normalizes referral code to lowercase
 * @param {string} sourceRef - Source referral code
 * @returns {string} Lowercase referral code or empty string
 */
const normalizeReferralCode = (sourceRef) => {
  return sourceRef ? sourceRef.toLowerCase() : ''
}

const submitData = async (formData) => {
  const { contactInfo, schedule } = formData

  const splitSchedule = schedule ? schedule.split(',') : []

  // Handle schedule data safely
  const startTime = splitSchedule[0]
    ? moment.utc(splitSchedule[0]).toISOString()
    : null
  const endTime = splitSchedule[1]
    ? moment.utc(splitSchedule[1]).toISOString()
    : null

  try {
    const data = {
      // Eligibility
      utility: formData.howHeated,
      gasProvider: formData.gasProvider || '',
      account: formData.eversourceAccNo || '',
      electricProvider: formData.electricProvider,
      vHea: 'no',

      // ContactInfo
      firstname: contactInfo.firstName,
      lastname: contactInfo.lastName,
      secondaryFirstName: contactInfo.secondaryFirstName,
      secondaryLastName: contactInfo.secondaryLastName,
      email: contactInfo.email,
      phone: normalizePhoneNumber(contactInfo.phone),
      secondaryPhoneNumber: normalizePhoneNumber(contactInfo.secondaryPhoneNumber),
      // phoneType: contactInfo.phoneType,
      location: contactInfo.address,
      leadSource:
        urlParams.get('utm_source') === 'partners'
          ? 'Partners'
          : contactInfo.source,
      referralCode: normalizeReferralCode(contactInfo.sourceRef),
      Marketing_Referral_Code__c: normalizeReferralCode(contactInfo.sourceRef),
      callFireOptOut: !contactInfo.textOutIn,
      dataSharingAuthorized: contactInfo.dataSharingAuthorized,

      // Schedule (only if schedule data exists)
      region: splitSchedule[3] || null,
      start: startTime,
      end: endTime,
      hes_id: splitSchedule[2] || null,

      // PPC Tracking
      web_form_destination: urlParams.get('webformDestination') || '',
      ppc_ad_id: urlParams.get('utm_id') || '',
      ppc_campaign: urlParams.get('utm_campaign') || '',
      ppc_content: urlParams.get('utm_content') || '',
      ppc_medium: urlParams.get('utm_medium') || '',
      ppc_source: urlParams.get('utm_source') || '',
      ppc_term: urlParams.get('utm_term') || '',

      // Home Info
      reasonHere: formData.reasonHere,
      rentOrOwn: formData.rentOrOwn,
      homeAge: formData.homeAge,
      addToExisting: formData.addToExisting,
      // familySize will always have a value except for HEA/VEA singleFamily which would make familySize 1
      familySize: formData.familySize ? formData.familySize : '1',
      howHeated: formData.howHeated,
    }

    // Safely extract zip code from address
    let zipCode = null
    try {
      const addressParts = contactInfo.address.split(',')
      if (addressParts.length > 2) {
        const stateZipPart = addressParts[2].trim()
        const zipMatch = stateZipPart.match(/\b\d{5}\b/)
        zipCode = zipMatch ? zipMatch[0] : null
      }
    } catch (e) {
      console.warn('Could not parse zip code from address:', e)
    }
    
    if (
      formData.gasProvider &&
      zipCode &&
      config.columbiaGasZipCodes.otherRegions.includes(zipCode)
    )
      data.gasProvider = 'Columbia Gas'
    if (formData.path === 'hea') {
      data.massSave = formData.massSave
      data.isCondo = formData.isCondo
      data.ownerOrRenter = formData.ownerOrRenter ? formData.ownerOrRenter : ''
      data.singleOrMulti = formData.singleOrMulti
      data.unitNumber1 = formData.unitNumber1 ? formData.unitNumber1 : ''
      data.unitNumber2 = formData.unitNumber2 ? formData.unitNumber2 : ''
      data.unitNumber3 = formData.unitNumber3 ? formData.unitNumber3 : ''
      data.unitNumber4 = formData.unitNumber4 ? formData.unitNumber4 : ''
      data.languagePref = formData.contactInfo.languagePref
      // Only set schedule times if schedule data exists
      if (splitSchedule[0] && splitSchedule[1] && splitSchedule[4]) {
        data.startTime = moment
          .utc(splitSchedule[0])
          .subtract(splitSchedule[4], 'minutes')
          .format('HH:mm:ss')
        data.endTime = moment
          .utc(splitSchedule[1])
          .subtract(splitSchedule[4], 'minutes')
          .format('HH:mm:ss')
      } else {
        data.startTime = null
        data.endTime = null
      }
      data.isIncomeEligible = formData?.paymentAssistance === 'yes'
      data.hvacInterestAtIntake = formData.hvacInterestAtIntake === 'yes' ? 'Yes'
        : formData.hvacInterestAtIntake === 'no' ? 'Not at this time' : ''

      // TEMPORARY (SOF-1341): flag paused-gas HEA records (Eversource and Columbia Gas)
      // so Salesforce can identify this cohort and re-contact them when scheduling
      // resumes. Read from formData, not data.gasProvider, which is rewritten to
      // 'Columbia Gas' by zip above — that rewrite fires for any selected provider, so
      // the outgoing gasProvider cannot be used to tell the two cohorts apart.
      //
      // Deliberately NOT gated on isEversourceGasLead — CAP customers keep the SCSC flow
      // (so they are excluded from that predicate) but still need this marker.
      if (PAUSED_GAS_PROVIDERS.includes(formData.gasProvider)) {
        data.isEversourcePauseLead = true
      }
    }

    // HealthyHome uses the same /submit endpoint as HEA but with a distinct type code.
    // gasProvider and electricProvider are not collected, so they are explicitly removed.
    if (formData.path === 'healthyhome') {
      data.type = '000009'
      data.massSave = formData.massSave
      data.isCondo = formData.isCondo
      data.ownerOrRenter = formData.ownerOrRenter || ''
      data.singleOrMulti = formData.singleOrMulti
      data.unitNumber1 = formData.unitNumber1 || ''
      data.unitNumber2 = formData.unitNumber2 || ''
      data.unitNumber3 = formData.unitNumber3 || ''
      data.unitNumber4 = formData.unitNumber4 || ''
      data.languagePref = formData.contactInfo.languagePref
      data.isIncomeEligible = false
      if (splitSchedule[0] && splitSchedule[1] && splitSchedule[4]) {
        data.startTime = moment.utc(splitSchedule[0]).subtract(splitSchedule[4], 'minutes').format('HH:mm:ss')
        data.endTime = moment.utc(splitSchedule[1]).subtract(splitSchedule[4], 'minutes').format('HH:mm:ss')
      } else {
        data.startTime = null
        data.endTime = null
      }
    }

    if (formData.path === 'hvac') {
      const addressComponents = formData.contactInfo.addressComponents || {}
      const {
        street_number = '',
        route = '',
        locality = '',
        administrative_area_level_1 = '',
        postal_code = '',
      } = addressComponents
      const details = formData.contactInfo.details || ''
      const timeline = formData.contactInfo.timeline || ''
      const formattedStreet = `${street_number} ${route}`.trim()
      const formattedCity = locality
      data.type = '000100'
      data.details = details
      data.timeline = timeline
      data.Street = formattedStreet
      data.City = formattedCity
      data.State = administrative_area_level_1
      data.PostalCode = postal_code
      data.isMultiFamily = formData.familySize && formData.familySize === '1-4'
      data.familySize = '1'
      data.languagePref = formData.contactInfo.languagePref
      
      // Only set startTime/endTime if schedule data exists
      if (splitSchedule[0] && splitSchedule[1] && splitSchedule[4]) {
        data.startTime = moment
          .utc(splitSchedule[0])
          .subtract(splitSchedule[4], 'minutes')
          .format('HH:mm:ss')
        data.endTime = moment
          .utc(splitSchedule[1])
          .subtract(splitSchedule[4], 'minutes')
          .format('HH:mm:ss')
      } else {
        data.startTime = null
        data.endTime = null
      }
      
      await axios.post(
        `${getApiURL('hvacSalesSubmit')}/ohcsSubmit`,
        JSON.stringify(data),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
    } else {
      // else send data for hea appointments
      await axios.post(
        `${getApiURL('heaSubmit')}/submit`,
        JSON.stringify(data),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
    }
  } catch (error) {
    console.error('Error in submitData:', error)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)
    const errorMessage = error.response?.data?.message || error.message || 'Something went wrong and your request has not been sent.'
    throw new Error(errorMessage)
  }
}

export default submitData
